import React, {useEffect } from "react";
import "./Management.css";
import dash_5 from "../images/operational-dashboard.png";

const featureItems = [
  {
    number: "01",
    title: "Experienced care leadership",
    text: "Visible, accountable leadership from seasoned care-sector operators.",
  },
  {
    number: "02",
    title: "Governance and compliance",
    text: "Robust systems that keep quality, safety and CQC readiness at the centre.",
  },
  {
    number: "03",
    title: "Workforce optimisation",
    text: "A stable, capable workforce with the right deployment, culture and support.",
  },
  {
    number: "04",
    title: "Financial performance",
    text: "Commercial discipline that strengthens occupancy, efficiency and long-term value.",
  },
];

const managementItems = [
  "Full operational responsibility",
  "Interim and turnaround management",
  "CQC readiness and quality improvement",
  "Registered manager and leadership support",
  "Workforce planning and agency reduction",
  "Occupancy, referrals and fee strategy",
  "Financial controls and performance reporting",
  "Systems, policies and governance implementation",
];

const approachItems = [
  {
    number: "01",
    title: "Understand",
    text: "Review the service, people, systems, compliance and finances to establish a clear baseline.",
  },
  {
    number: "02",
    title: "Stabilise",
    text: "Take control of immediate risks, clarify accountability and introduce an effective operating rhythm.",
  },
  {
    number: "03",
    title: "Improve",
    text: "Deliver a focused plan that strengthens care quality, workforce, occupancy and profitability.",
  },
  {
    number: "04",
    title: "Sustain",
    text: "Embed capable leaders, transparent reporting and systems that make better performance last.",
  },
];

const Operational_Management = () => {
  // open page on top //
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // open page on top //

  return (
    <main className="operational-page">
      {/* ================= HERO ================= */}
      <section className="om-hero">
        <div className="om-hero-shape"></div>

        <div className="om-container om-hero-grid">
          <div className="om-hero-content">
            <span className="om-eyebrow">OPERATIONAL MANAGEMENT</span>
            <h1>Expert operational management for stronger care businesses</h1>

            <p className="om-hero-description">
              We operate care homes on behalf of owners, investors and
              operators—strengthening governance, improving performance and
              building sustainable, high-quality services.
            </p>

            <div className="om-hero-buttons">
              <button className="om-btn om-btn-primary">
                <a href="#CTA">Book an operational review</a>
              </button>

              <button className="om-btn om-btn-outline">
                <a href="#manage">Explore our approach</a>
              </button>
            </div>

            <div className="om-hero-points">
              <span>Hands-on leadership</span>
              <i>•</i>
              <span>CQC-focused governance</span>
              <i>•</i>
              <span>Commercial discipline</span>
            </div>
          </div>

          <div className="om-hero-image-wrapper">
            <img
              src={dash_5}
              alt="Operational management"
              className="om-hero-image"
            />

            <div className="om-image-label">
              <span>OPERATIONAL CLARITY</span>
              <strong>From boardroom to care floor</strong>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURE STRIP ================= */}
      <section className="om-feature-strip">
        <div className="om-feature-container">
          {featureItems.map((item) => (
            <div className="om-feature" key={item.number}>
              <span className="om-feature-number">{item.number}</span>

              <div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section className="om-intro">
        <div className="om-container om-intro-grid">
          <div className="om-intro-left">
            <span className="om-eyebrow">COMPLETE OPERATIONAL OVERSIGHT</span>

            <h2>
              Operational grip.
              <br />
              Lasting improvement.
            </h2>
          </div>

          <div className="om-intro-right">
            <h3>
              We take full operational responsibility—bringing clarity,
              consistency and control to every aspect of care-home performance.
            </h3>

            <p>
              Whether a service needs stabilising, turning around or scaling,
              our team works inside the operation. We align care quality and
              commercial performance so neither is improved at the expense of
              the other.
            </p>
          </div>
        </div>
      </section>

      {/* ================= WHAT WE MANAGE ================= */}
      <section className="om-manage" id="manage">
        <div className="om-container">
          <div className="om-manage-heading">
            <div className="om-manage-label">
              <span className="om-eyebrow">WHAT WE MANAGE</span>
            </div>

            <div className="om-manage-title">
              <h2>
                A complete operating
                <br />
                partner for care
                <br />
                businesses
              </h2>
            </div>

            <div className="om-manage-description">
              <p>
                Flexible support from a focused improvement programme to full
                day-to-day operational management.
              </p>
            </div>
          </div>

          <div className="om-check-grid">
            {managementItems.map((item, index) => (
              <div className="om-check-item" key={index}>
                <span className="om-check">✓</span>

                <strong>{item}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= APPROACH ================= */}
      <section className="om-approach">
        <div className="om-container">
          <div className="om-approach-heading">
            <div>
              <span className="om-eyebrow">OUR APPROACH</span>
            </div>

            <h2>
              Structured enough to measure.
              <br />
              Practical enough to work.
            </h2>
          </div>

          <div className="om-approach-grid">
            {approachItems.map((item) => (
              <div className="om-approach-card" key={item.number}>
                <span className="om-approach-number">{item.number}</span>

                <h3>{item.title}</h3>

                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= AUDIENCE ================= */}
      <section className="om-audience">
        <div className="om-container om-audience-grid">
          <div className="om-audience-card om-owner-card">
            <span className="om-eyebrow">FOR OWNERS AND OPERATORS</span>

            <h2>
              More control, stronger
              <br />
              teams and better
              <br />
              outcomes
            </h2>

            <p>
              Gain experienced operational leadership without losing sight of
              your values, ambitions or residents.
            </p>
          </div>

          <div className="om-audience-card om-investor-card">
            <span className="om-eyebrow">FOR INVESTORS AND LENDERS</span>

            <h2>
              Protect value through
              <br />
              expert oversight
            </h2>

            <p>
              Improve visibility, manage risk and create a credible path from
              acquisition or underperformance to sustainable value.
            </p>
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="om-cta" id="CTA">
        <div className="om-container om-cta-inner">
          <div className="om-cta-content">
            <span className="om-eyebrow">
              START A CONFIDENTIAL CONVERSATION
            </span>

            <h2>Let’s strengthen your care business.</h2>

            <p>
              Tell us where the pressure is. We’ll help you understand the
              priorities and the right level of operational support.
            </p>
          </div>

          <button className="om-cta-button">
            <a href="mailto:info@totalcareadvisory.com?subject=Total%20Care%20Rota%20Demo">
              Book an operational review
            </a>
          </button>
        </div>
      </section>
    </main>
  );
};

export default Operational_Management;
